/**
 * Журнал действий администратора — тот самый `admin_audit`.
 *
 * Показывается не отдельным разделом, а прямо там, где случились правки:
 * под редактором конфига и в карточке игрока. Причина простая: требование
 * «каждое изменяющее действие оставляет след» либо проверяется глазами
 * владельца за две секунды, либо не проверяется никогда — запросом в базу
 * его никто проверять не станет.
 */

import { useEffect, useState } from 'react'
import { request } from '../api/client.js'
import type { AuditRow } from '../api/types.js'
import { diffLeaves } from '../lib/diff.js'
import { formatDate } from './ui.js'

export interface AuditTrailProps {
  entity: string
  entityId?: string
  /** Меняется после сохранения — журнал перечитывается. */
  refreshKey?: number
  limit?: number
}

export function AuditTrail({ entity, entityId, refreshKey = 0, limit = 8 }: AuditTrailProps): JSX.Element {
  const [rows, setRows] = useState<AuditRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    request<{ rows: AuditRow[] }>('/admin/audit', {
      query: { entity, entity_id: entityId, limit },
    })
      .then((data) => {
        if (cancelled) return
        setRows(data.rows)
        setError(null)
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [entity, entityId, refreshKey, limit])

  if (loading && rows.length === 0) return <div className="table__state">Loading…</div>
  if (error !== null) return <div className="table__state" style={{ color: 'var(--danger)' }}>{error}</div>
  if (rows.length === 0) {
    return <div className="table__state">No changes yet — the trail is empty.</div>
  }

  return (
    <div>
      {rows.map((row) => (
        <div className="audit__row" key={row.id}>
          <div className="audit__when">
            {formatDate(row.createdAt)}
            <br />
            {row.adminEmail ?? row.adminId ?? 'admin deleted'}
          </div>
          <div>
            <span className="badge">{row.action}</span>{' '}
            <span className="mono muted">{row.entityId ?? row.entity}</span>
            <div className="audit__diff">{describe(row)}</div>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Человеческое описание строки аудита. Для известных действий — по делу,
 * для незнакомых — сырые before/after: журнал не должен молчать о правке
 * только потому, что о ней не знает эта функция.
 */
function describe(row: AuditRow): string {
  if (row.action === 'wallet.grant') {
    const after = row.after as { grant?: { currency?: string; amount?: number; note?: string | null } } | null
    const grant = after?.grant
    if (grant !== undefined) {
      const sign = (grant.amount ?? 0) > 0 ? '+' : '−'
      const note = grant.note === null || grant.note === undefined || grant.note === '' ? '' : ` · ${grant.note}`
      const before = row.before as Record<string, number> | null
      const balance = before === null ? '' : ` (was ${String(before[grant.currency ?? 'gold'] ?? 0)})`
      return `${grant.currency ?? '?'} ${sign}${String(Math.abs(grant.amount ?? 0))}${balance}${note}`
    }
  }

  if (row.action === 'config.update' || row.action === 'content.reclaim') {
    const before = (row.before as { value?: unknown } | null)?.value
    const after = (row.after as { value?: unknown } | null)?.value
    const changes = diffLeaves(before, after)

    // У возврата к файлу главное не «что поменялось», а «кто и откуда
    // забрал строку у админки»: значение могло и совпасть.
    const prefix =
      row.action === 'content.reclaim'
        ? `reclaimed by the file (${(row.after as { via?: string } | null)?.via === 'cli' ? 'CLI' : 'admin panel'})\n`
        : ''

    if (changes.length > 0) return prefix + changes.join('\n')
    if (row.action === 'content.reclaim') return `${prefix}the value already matched the file`
    if (before === undefined) return 'config created'
  }

  return `${JSON.stringify(row.before)} → ${JSON.stringify(row.after)}`
}
